import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useAuth } from './AuthContext';
import { getFxRates, getMyCurrency, setMyCurrency } from '../lib/api';
import {
  BASE,
  CURRENCIES,
  fmtMinorText,
  fmtMoneyText,
  fmtPayText,
  formatInr,
  getMoneyState,
  moneyParts,
  resetMoneyState,
  setMoneyState,
  subscribeMoney,
} from '../lib/money';
import { logger } from '../utils/logger';

const CurrencyContext = createContext(null);

/**
 * Loads the signed-in viewer's display currency and today's rates into the
 * money store. Anonymous visitors stay on INR. Never blocks rendering.
 */
export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null); // /me/currency payload
  const userId = user?.user_id;

  useEffect(() => {
    if (!userId) {
      setInfo(null);
      resetMoneyState();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const [ratesRes, meRes] = await Promise.allSettled([getFxRates(), getMyCurrency()]);
      if (cancelled) return;
      const patch = {};
      if (ratesRes.status === 'fulfilled') {
        patch.rates = ratesRes.value?.data?.rates;
        patch.fetchedAt = ratesRes.value?.data?.fetched_at || null;
      } else {
        logger.warn('fx rates load failed', { error: ratesRes.reason?.message });
      }
      if (meRes.status === 'fulfilled') {
        patch.currency = meRes.value?.data?.effective_currency || BASE;
        setInfo(meRes.value?.data || null);
      }
      setMoneyState(patch);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  /** Save the viewer's choice; pass null to go back to the country default. */
  const changeCurrency = useCallback(async (code) => {
    const res = await setMyCurrency(code);
    setInfo(res.data);
    setMoneyState({ currency: res.data?.effective_currency || BASE });
    return res.data;
  }, []);

  const value = useMemo(() => ({ info, changeCurrency }), [info, changeCurrency]);
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/**
 * Re-renders when the display currency or rates change.
 * fmt(inr)      → viewer-currency text ("$12.40", or "₹1,030" for INR viewers)
 * fmtPay(inr)   → pay-screen text ("₹1,030 ≈ $12.40")
 * fmtMinor(m,c) → 3PL integer minor units in currency c
 * fmtInr(inr)   → always INR
 */
export function useMoney() {
  const s = useSyncExternalStore(subscribeMoney, getMoneyState, getMoneyState);
  const ctx = useContext(CurrencyContext);
  return useMemo(() => ({
    currency: s.currency,
    rates: s.rates,
    fetchedAt: s.fetchedAt,
    currencies: CURRENCIES,
    info: ctx?.info || null,
    changeCurrency: ctx?.changeCurrency,
    fmt: (inr, opts) => fmtMoneyText(inr, opts),
    fmtPay: (inr, opts) => fmtPayText(inr, opts),
    fmtMinor: (minor, cur, opts) => fmtMinorText(minor, cur, opts),
    fmtInr: (inr, opts) => formatInr(inr, opts),
    parts: (inr, opts) => moneyParts(inr, opts),
  }), [s, ctx]);
}

/**
 * <Money inr={1030} />          → "$12.40" with "₹1,030" on hover
 * <Money inr={1030} secondary /> → "$12.40" plus a small "₹1,030"
 * <Money inr={1030} payment />   → "₹1,030" plus a small "≈ $12.40"
 */
export function Money({ inr, payment = false, secondary = false, decimals, className = '', secondaryClassName = '' }) {
  useMoney();
  const p = moneyParts(inr, { payment, decimals });
  const showSecondary = p.secondary && (payment || secondary);
  return (
    <span className={className} title={p.title} data-testid="money">
      {p.primary}
      {showSecondary && (
        <span className={`ml-1 text-[0.8em] font-normal opacity-70 ${secondaryClassName}`}>
          {payment ? p.secondary : `(${p.secondary})`}
        </span>
      )}
    </span>
  );
}
